"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { usePathname } from "next/navigation";
import HeaderV2 from "../global/partials/HeaderV2";
import EcommerceHeader from "../pageComponents/ecommerce/EcommerceHeader";
import { PageFuturesLPSchema } from "@/sanity/pages/PageFuturesLPTemplate";
import HeroSection from "../pageComponents/futuresLP/HeroSection";
import OfferSection from "../pageComponents/anniversary/OfferSection";
import FeaturesSection from "../pageComponents/anniversary/FeaturesSection";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import FeaturedQuoteSection from "../pageComponents/global/sections/FeaturedQuoteSection";
import RulesSection from "../pageComponents/futuresLP/RulesSection";
import ComparisonSection from "../pageComponents/futuresLP/ComparisonSection";
import DifferentiatorSection from "../pageComponents/futuresLP/DifferentiatorSection";
import NetworkSection from "../pageComponents/futuresLP/NetworkSection";
import CTASection from "../pageComponents/futuresLP/CTASection";
import IQCLogo from "../global/ui/IQCLogo";
import {
  resolveI18nRichtext,
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";
import { resolvePageLink } from "@/sanity/globals/CTA";

export default function PageFuturesLPJSX({
  data,
}: {
  data: PageFuturesLPSchema;
}) {
  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  const footer = data.settings?.footer;
  const disclaimerText = resolveI18nRichtext(
    footer?.disclaimer,
    footer?.disclaimerI18n,
    language,
  );
  const legalLinks =
    footer?.navColumns?.find(
      (col) =>
        resolveI18nText(col.heading, col.i18n, language)?.toUpperCase() ===
        "POLICY",
    )?.links ?? [];

  return (
    <>
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType="main"
          language={data.settings?.language}
        />
      )}

      <EcommerceHeader data={data as any} className="block" />

      <HeroSection data={data} />
      <FeaturedQuoteSection
        data={data as any}
        className="[&_.quoteText]:lg:w-[160rem]"
      />
      <OfferSection data={data as any} />
      <ScrollingBanner
        className="bg-[#F4F5F5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
      <FeaturesSection
        data={data as any}
        className="pt-[7rem] lg:pt-[12rem] lg:prose-h2:text-[6.5rem]"
      />

      <RulesSection data={data} />

      <ScrollingBanner
        className="bg-[#f4f5f5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
      <ComparisonSection data={data} />
      <DifferentiatorSection data={data} />
      <NetworkSection data={data} />
      <CTASection data={data} />

      {(disclaimerText || !!legalLinks.length) && (
        <section className="relative w-full bg-[#f4f4f4] py-[6rem] px-[3rem] lg:py-[8rem] lg:px-[4rem]">
          <div className="flex flex-col mx-auto gap-[4rem] w-[min(140rem,100%)]">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-[2rem]">
              <p className="m-0 font-geist text-black/70 text-[1.75rem] lg:text-[2rem] flex-1 max-sm:order-1">
                © IQ Capital - All rights reserved
              </p>
              <div className="flex-shrink-0">
                <IQCLogo width="15.5rem" height="2.9rem" fill="#001b14" />
              </div>
              {!!legalLinks.length && (
                <ul className="max-sm:order-2 flex flex-1 flex-wrap items-center justify-center lg:justify-end p-0 m-0 list-none gap-x-[1rem] gap-y-[0.5rem]">
                  {legalLinks.map((item, idx) => {
                    const href = resolvePageLink(item.link, language) ?? "#";
                    const isExternal =
                      item.link?.externalLink && item.link?.openInNewTab;
                    const isLast = idx === legalLinks.length - 1;
                    return (
                      <li
                        key={idx}
                        className="m-0 flex items-center gap-[1rem]"
                      >
                        <a
                          href={href}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          className="transition-all hover:underline text-black/80 text-[1.75rem] underline-offset-4 font-geist lg:text-[2rem]"
                        >
                          {resolveI18nText(item.title, item.i18n, language)}
                        </a>
                        {!isLast && (
                          <span className="text-black/40 text-[1.75rem] lg:text-[2rem]">
                            /
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="w-full border-t h-[.125rem] border-t-black/10" />

            {disclaimerText && (
              <div className="richtext richtext-center prose-p:text-black/60 prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-geist">
                <PortableText
                  value={disclaimerText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
